// src/components/Workout/Workout.js
import React, { useState, useContext, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import "../style.css";
import WorkoutHeader from "./WorkoutHeader";
import ExerciseList from "./ExerciseList";
import QRCodeSection from "./QRCodeSection";
import { processExercises, convertToSeconds, parseReps, formatFitnessGoal, formatExperience, formatWorkout, formatTime, formatIntensity } from "../../utils/workoutUtils";
import CustomFooter from "../CustomFooter.js";
import LanguageContext from "../../../context/LanguageContext";

// Import data
const jsonDataEn = require("../../data/data-en.json");
const jsonDataFr = require("../../data/data-fr.json");

const Workout = ({ gymName, footer }) => {
  const { language } = useContext(LanguageContext);
  const data = language === "en" ? jsonDataEn.data : jsonDataFr.data;

  const location = useLocation();
  const rawExercises = location.state?.rawExercises || [];
  const formData = location.state?.formData || {};

  const [openIndex, setOpenIndex] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [selectedReplacements, setSelectedReplacements] = useState({});
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [sharedReplacements, setSharedReplacements] = useState([]);

  // Process the raw exercises once they are available
  useEffect(() => {
    if (rawExercises.length > 0) {
      const { processedExercises, sharedReplacements } = processExercises(rawExercises, formData);
      setExercises(processedExercises);
      setSharedReplacements(sharedReplacements);
    }
  }, [rawExercises, formData]);

  // Generate the QR code URL based on processed exercises
  useEffect(() => {
    const withIds = exercises.filter(({ exercise_id }) => {
      const hasId = exercise_id !== undefined;
      if (!hasId) console.warn("Skipping exercise with no ID", { exercise_id });
      return hasId;
    });

    const minimalData = withIds.map(({ exercise_id, Sets, Reps, Rest }) => {
      // Fallback defaults if data is missing
      if (exercise_id === undefined || Sets === undefined || Reps === undefined || Rest === undefined) {
        console.warn("Incomplete exercise data", { exercise_id, Sets, Reps, Rest });
      }
      const sets = typeof Sets === "number" ? Sets : parseInt(Sets, 10) || 0;
      const reps = parseReps(Reps);
      const restInSeconds = convertToSeconds(Rest);
      return `${exercise_id},${sets},${reps},${restInSeconds}`;
    });
    const dataString = minimalData.join("|");
    const baseURL = `https://flxdeck.com/${gymName}workoutdetails`;
    setQrCodeUrl(`${baseURL}?data=${dataString}`);
  }, [exercises]);

  const handleReplaceSubmit = (index) => {
    const replacementIndex = selectedReplacements[index];
    if (replacementIndex === undefined) return;

    setExercises((prevExercises) => {
      const newExercises = [...prevExercises];
      const originalExercise = newExercises[index];
      const replacementExercise = originalExercise.replacement[replacementIndex];
      if (!replacementExercise) return newExercises;

      // Replace the exercise with the selected replacement
      newExercises[index] = { ...replacementExercise };

      // Check if using the shared replacements array
      const isSharedReplacement = originalExercise.replacement === sharedReplacements;
      if (isSharedReplacement) {
        const updatedSharedReplacements = [...sharedReplacements];
        updatedSharedReplacements[replacementIndex] = originalExercise;
        setSharedReplacements(updatedSharedReplacements);
        newExercises[index].replacement = updatedSharedReplacements;
      } else {
        const updatedReplacements = originalExercise.replacement.slice();
        updatedReplacements[replacementIndex] = originalExercise;
        newExercises[index].replacement = updatedReplacements;
      }
      return newExercises;
    });

    setSelectedReplacements((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  const handleReplacementChange = (index, altIndex) => {
    setSelectedReplacements((prev) => ({ ...prev, [index]: altIndex }));
  };

  const toggleOpen = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  const handleRemoveOptional = (indexToRemove) => {
    setExercises((curr) => curr.filter((_, idx) => idx !== indexToRemove));
  };

  return (
    <section className="hero-section">
      <div className="container hero-content">
        <WorkoutHeader formData={formData} formatters={{ formatFitnessGoal, formatExperience, formatWorkout, formatTime, formatIntensity }} />
        <ExerciseList
          exercises={exercises}
          onRemoveOptional={handleRemoveOptional}
          openIndex={openIndex}
          toggleOpen={toggleOpen}
          handleReplacementChange={handleReplacementChange}
          handleReplaceSubmit={handleReplaceSubmit}
          selectedReplacements={selectedReplacements}
        />
        <QRCodeSection qrCodeUrl={qrCodeUrl} gymName={gymName} />
      </div>
      {/* <div className="footer-fixed"> */}
      <CustomFooter line1={footer} line2={data.footer} />
      {/* </div> */}
    </section>
  );
};

export default Workout;
